import React, { FC, ReactElement, useContext, useState } from 'react'

import { Visibility } from 'globalTypes'

import { gql, useQuery } from '@apollo/client'
import { useLingui } from '@lingui/react'
import { Context as AuthorizationsContext } from 'Root/App/Contexts/Authorizations'
import ShareDialogSwitch from 'Root/App/Header/Buttons/ShareOrCopy/ShareDialogSwitch'
import { Context } from 'Root/Contexts/Share'
import { isMatching } from 'lib/breakpoint-helper'
import { getElementType } from 'lib/get-element-type'
import { shareCallback } from 'lib/share-callback'

interface ShareOrCopyProps {
  id: string
  openDialogButton: ReactElement
}

const canUseNavigatorShare = Boolean(
  typeof window !== 'undefined' &&
    window.navigator.share &&
    window.navigator.canShare
)

const GET_SHARE_OR_COPY_ELEMENT_VISIBILITY = gql`
  query GetShareOrCopyElementVisibility($id: ID!) {
    element(id: $id) {
      __typename

      ... on Collection {
        id
        visibility
      }

      ... on Content {
        id
        visibility
      }

      ... on Folder {
        id
        visibility
      }
    }
  }
`

const ShareOrCopy: FC<ShareOrCopyProps> = ({ id, openDialogButton }) => {
  const { i18n } = useLingui()
  const [isDialogOpen, setDialogOpen] = useState(false)
  const shareData = useContext(Context)
  const { setElementId, subscriptionPlanCode, userSubscriptionLoading } =
    useContext(AuthorizationsContext)

  const { data, loading } = useQuery(GET_SHARE_OR_COPY_ELEMENT_VISIBILITY, {
    variables: {
      id,
    },
  })

  const isMobile = isMatching('sm', true)

  const isFetchingData = loading || userSubscriptionLoading

  const handleOnConfirm = (visibility: Visibility) => {
    if (visibility !== Visibility.mine) {
      setElementId(id)
    }
  }

  const handleCopyOnClick = shareCallback(
    canUseNavigatorShare && isMobile && window.navigator.canShare(shareData),
    shareData,
    i18n
  )

  const handleShareOnClick =
    data?.element?.visibility !== Visibility.mine &&
    subscriptionPlanCode !== 'essential'
      ? () => setElementId(id)
      : () => setDialogOpen((isDialogOpen) => !isDialogOpen)

  return (
    <>
      {React.cloneElement(openDialogButton, {
        onClick: shareData.canManage ? handleShareOnClick : handleCopyOnClick,
        disabled: isFetchingData,
      })}
      <ShareDialogSwitch
        id={id}
        isOpen={isDialogOpen}
        onClose={() => setDialogOpen((isDialogOpen) => !isDialogOpen)}
        onConfirm={handleOnConfirm}
        elementType={getElementType(shareData.type)}
        subscriptionPlanCode={subscriptionPlanCode}
        visibility={data?.element?.visibility}
      />
    </>
  )
}

export default ShareOrCopy
